/**
 * SecuritySurfacePanel — shared shell for CSK security-surface displays.
 *
 * Single responsibility: render the standard heading, theorem-scope line,
 * summary paragraph, optional metric row, and a body slot. App-owned
 * surfaces compose this shell with their own body content so visual
 * treatment stays consistent without centralizing app registries.
 *
 * Composition: builds on the shared `Card` primitive so the surface
 * (border, padding, white background, rounded corners) stays consistent
 * with every other card in the demo UI library.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import type { DemoTheorem } from "../types.js";
import { GRAY } from "../design-system/tokens.js";
import { Card } from "./Card.js";

export interface SecuritySurfaceMetric {
  readonly label: string;
  readonly value: number | string;
}

export interface SecuritySurfacePanelProps {
  readonly heading: string;
  readonly summary: React.ReactNode;
  readonly theorem?: DemoTheorem | readonly DemoTheorem[];
  readonly metrics?: readonly SecuritySurfaceMetric[];
  /**
   * Optional chart slot rendered between the metric row and the body
   * content. Use a token-driven chart primitive from
   * `@csk/demo-ui/components/charts` so colours stay aligned with
   * `tokens.data.json`. The panel does not impose a height — pass a
   * chart whose own `height` prop fits the surrounding layout.
   */
  readonly chart?: React.ReactNode;
  readonly ariaLabel: string;
  readonly children?: React.ReactNode;
}

function joinTheoremNames(theorem: DemoTheorem | readonly DemoTheorem[]): string {
  const list: readonly DemoTheorem[] = Array.isArray(theorem) ? theorem : [theorem];
  return list.map((item) => item.name).join(" + ");
}

export const SecuritySurfacePanel = React.memo(function SecuritySurfacePanel({
  heading,
  summary,
  theorem,
  metrics,
  chart,
  ariaLabel,
  children,
}: SecuritySurfacePanelProps): React.JSX.Element {
  // React.useId() guarantees a unique ID per panel instance even when
  // multiple panels with the same `ariaLabel` are mounted on one page.
  const reactId = React.useId();
  const describedById = `csk-surface-summary-${reactId}`;
  const theoremNames = theorem !== undefined ? joinTheoremNames(theorem) : null;

  return (
    <Card aria-label={ariaLabel} aria-describedby={describedById}>
      <header style={{ marginBottom: 8 }}>
        <h3 style={{ margin: 0, color: GRAY[900], fontSize: "1rem" }}>{heading}</h3>
        {theoremNames !== null && (
          <div style={{ marginTop: 4, color: GRAY[700], fontSize: 12 }}>
            Theorem scope: <strong>{theoremNames}</strong>
          </div>
        )}
      </header>

      {/*
        Rendered as a <div> (not a <p>) because `summary` is typed
        `React.ReactNode` and callers may pass block-level content
        (lists, fragments wrapping <strong>/<code>, etc.) that would
        be invalid HTML inside a <p>. The describedById on the section
        carries the screen-reader association, so no semantic loss.
      */}
      <div id={describedById} style={{ margin: 0, color: GRAY[700] }}>
        {summary}
      </div>

      {metrics !== undefined && metrics.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            marginTop: 10,
            color: GRAY[700],
            fontSize: 13,
          }}
        >
          {metrics.map((metric) => (
            <span key={metric.label}>
              {metric.label}: <strong>{metric.value}</strong>
            </span>
          ))}
        </div>
      )}

      {chart !== undefined && (
        <div data-csk-surface-chart style={{ marginTop: 12 }}>
          {chart}
        </div>
      )}

      {children !== undefined && <div style={{ marginTop: 12 }}>{children}</div>}
    </Card>
  );
});
